import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@mui/material/styles';
import {
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Box,
  Grow,
  Alert,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import {
  Visibility, VisibilityOff, PriorityHigh,
} from '@mui/icons-material';
import DisabledTooltip from '../../../tooltips/disabled-tooltip/DisabledTooltip';
import FormControlTooltip from '../../../../modules/admissions/components/form-input/form-control-tooltip/FormControlTooltip';
import getAriaDescribedBy from '../../helpers/ariaDescribedBy';
import { styles } from './styles';

export const TextField = ({
  ariaDescribedBy,
  ariaLabelledBy,
  autoComplete,
  control,
  dataCy,
  customError,
  defaultValue,
  disabled,
  disableHelperText,
  disabledTooltipText,
  tooltipContextId,
  fullWidth,
  id,
  isLoading,
  label,
  name,
  onBlurCallback,
  onChangeCallback,
  placeholder,
  required,
  subHelperText,
  type,
  variant,
  className,
  dataStyle,
  tooltipSev,
  tooltipMsg,
  onPaste,
  enableShrink,
  characterCounter,
  multiline,
  rows,
  minRows,
  maxRows,
  alertText,
  alertClose,
  controlFocus,
  setControlFocus,
}) => {
  const theme = useTheme();
  const classes = styles(theme);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [charFocus, setCharFocus] = useState(true);
  const [hideAlert, setHideAlert] = useState(true);

  return (
    <Controller
      render={({
        field: {
          onChange, onBlur, value, ref,
        },
        // Waiting for isValidating to properly return (next update?)
        fieldState: { invalid, error },
      }) => (
        <>
          {!!alertText && (
            <div className="fca">
              <Grow
                in={!hideAlert}
                mountOnEnter
                unmountOnExit
                timeout={{ enter: 250, exit: 1000 }}
              >
                <Alert
                  icon={<PriorityHigh fontSize="inherit" />}
                  severity="warning"
                  sx={classes.alert}
                  className="alert fcAlert fcTooltip"
                >
                  {alertText}
                  <span className="arrow" />
                </Alert>
              </Grow>
            </div>
          )}
          {tooltipMsg === '' ? (
            <MuiTextField
              data-container={customError.customErrorCondition === error?.type
              && customError.customErrorComponent ? `${id}-wrapper` : ''}
              data-style={dataStyle}
              id={id}
              name={name}
              value={value}
              disabled={disabled}
              label={label}
              placeholder={placeholder}
              fullWidth={fullWidth}
              error={invalid}
              type={showPassword ? 'text' : type}
              variant={variant}
              required={required}
              autoComplete={autoComplete}
              onFocus={() => {
                if (alertText) {
                  setHideAlert(false);
                  setControlFocus(false);
                }
                setFocused(true);
              }}
              onBlur={event => {
                if (alertText) {
                  if (document.querySelector('body[modality=keyboard]')) {
                    if (controlFocus) setHideAlert(true);
                  } else if (alertClose) {
                    setHideAlert(true);
                  }
                  if (value) setHideAlert(true);
                }
                onBlur(event);
                onBlurCallback(event);
                setFocused(false);
                if (characterCounter.enabled) setCharFocus(false);
              }}
              onChange={event => {
                setFocused(false);
                onChange(event);
                onChangeCallback(event);
                if (alertText) setControlFocus(true);
                if (characterCounter.enabled) setCharFocus(true);
              }}
              {...(enableShrink && {
                InputLabelProps: { shrink: !!value },
              })}
              InputProps={{
                'aria-labelledby': ariaLabelledBy,
                'aria-describedby': getAriaDescribedBy(
                  ariaDescribedBy,
                  `${id}-helper-text`,
                  !disableHelperText && !!error?.message,
                ),
                'data-cy': dataCy,
                endAdornment: (
                  <InputAdornment position="end">
                    {disabled && (
                    <DisabledTooltip
                      placement="top"
                      id={id}
                      title={disabledTooltipText || 'This field is disabled'}
                      iconSize="small"
                      popperProps={{}}
                      tooltipContextId={tooltipContextId}
                    />
                    )}
                    {type === 'password' && (
                    <IconButton
                      aria-label="Toggle password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      size="large"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                    )}
                    {isLoading && <CircularProgress size={24} />}
                    {(focused || charFocus) && characterCounter.enabled && (
                      <Box
                        component="p"
                        sx={{
                          ...(invalid && classes.characterCountInvalid),
                          ...classes.characterCount,
                        }}
                      >
                        {(value || '').length}
                        /
                        {characterCounter.maxCharacters}
                      </Box>
                    )}
                  </InputAdornment>
                ),
              }}
              helperText={!disableHelperText && error?.message}
              inputRef={ref}
              sx={className}
              onPaste={onPaste}
              multiline={multiline}
              rows={rows}
              minRows={minRows}
              maxRows={maxRows}
            />
          ) : (
            <FormControlTooltip
              tooltipSeverity={tooltipSev}
              tooltipMessage={tooltipMsg}
            >
              <MuiTextField
                data-container={customError.customErrorCondition === error?.type
                && customError.customErrorComponent ? `${id}-wrapper` : ''}
                data-style={dataStyle}
                id={id}
                name={name}
                value={value}
                disabled={disabled}
                label={label}
                placeholder={placeholder}
                fullWidth={fullWidth}
                error={invalid}
                type={showPassword ? 'text' : type}
                variant={variant}
                required={required}
                autoComplete={autoComplete}
                onFocus={() => {
                  if (alertText) {
                    setHideAlert(false);
                    setControlFocus(false);
                  }
                  setFocused(true);
                }}
                onBlur={event => {
                  if (alertText) {
                    if (document.querySelector('body[modality=keyboard]')) {
                      if (controlFocus) setHideAlert(true);
                    } else if (alertClose) {
                      setHideAlert(true);
                    }
                    if (value) setHideAlert(true);
                  }
                  onBlur(event);
                  onBlurCallback(event);
                  setFocused(false);
                  if (characterCounter.enabled) setCharFocus(false);
                }}
                onChange={event => {
                  setFocused(false);
                  onChange(event);
                  onChangeCallback(event);
                  if (alertText) setControlFocus(true);
                  if (characterCounter.enabled) setCharFocus(true);
                }}
                {...(enableShrink && {
                  InputLabelProps: { shrink: !!value },
                })}
                InputProps={{
                  'aria-labelledby': ariaLabelledBy,
                  'aria-describedby': getAriaDescribedBy(
                    ariaDescribedBy,
                    `${id}-helper-text`,
                    !disableHelperText && !!error?.message,
                  ),
                  'data-cy': dataCy,
                  endAdornment: (
                    <InputAdornment position="end">
                      {disabled && (
                      <DisabledTooltip
                        placement="top"
                        id={id}
                        title={disabledTooltipText || 'This field is disabled'}
                        iconSize="small"
                        popperProps={{}}
                        tooltipContextId={tooltipContextId}
                      />
                      )}
                      {type === 'password' && (
                      <IconButton
                        aria-label="Toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        size="large"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                      )}
                      {isLoading && <CircularProgress size={24} />}
                      {(focused || charFocus) && characterCounter.enabled && (
                        <Box
                          component="p"
                          sx={{
                            ...(invalid && classes.characterCountInvalid),
                            ...classes.characterCount,
                          }}
                        >
                          {(value || '').length}
                          /
                          {characterCounter.maxCharacters}
                        </Box>
                      )}
                    </InputAdornment>
                  ),
                }}
                helperText={!disableHelperText && error?.message}
                inputRef={ref}
                sx={className}
                multiline={multiline}
                rows={rows}
                minRows={minRows}
                maxRows={maxRows}
              />
            </FormControlTooltip>
          )}
          {customError.customErrorCondition === error?.type
              && customError.customErrorComponent && (
                <customError.customErrorComponent />
          )}
          {focused && subHelperText}
        </>
      )}
      name={name}
      control={control}
      defaultValue={defaultValue}
    />
  );
};

TextField.propTypes = {
  ariaDescribedBy: PropTypes.string,
  ariaLabelledBy: PropTypes.string,
  autoComplete: PropTypes.oneOf([
    'on',
    'off',
    'name',
    'given-name',
    'additional-name',
    'family-name',
    'address-line2',
    'address-level2',
    'email',
    'username',
    'new-password',
    'current-password',
    'street-address',
    'address-line1',
    'country',
    'country-name',
    'postal-code',
    'bday',
    'tel',
  ]),
  control: PropTypes.shape().isRequired,
  customError: PropTypes.shape({
    customErrorCondition: PropTypes.string,
    customErrorComponent: PropTypes.oneOfType([
      PropTypes.element,
      PropTypes.func,
    ]),
  }),
  dataCy: PropTypes.string,
  defaultValue: PropTypes.string,
  disabled: PropTypes.bool,
  disableHelperText: PropTypes.bool,
  disabledTooltipText: PropTypes.string,
  tooltipContextId: PropTypes.string,
  fullWidth: PropTypes.bool,
  id: PropTypes.string,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  onBlurCallback: PropTypes.func,
  onChangeCallback: PropTypes.func,
  onPaste: PropTypes.func,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  subHelperText: PropTypes.element,
  type: PropTypes.oneOf(['text', 'password', 'number', 'tel', 'email', 'date']),
  variant: PropTypes.string,
  className: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.func,
    PropTypes.oneOf([null, undefined]),
    PropTypes.string, // included to catch misuse
  ]),
  dataStyle: PropTypes.bool,
  tooltipSev: PropTypes.string,
  tooltipMsg: PropTypes.string,
  alertText: PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.func]),
  alertClose: PropTypes.bool,
  controlFocus: PropTypes.bool,
  setControlFocus: PropTypes.func,
  enableShrink: PropTypes.bool,
  characterCounter: PropTypes.shape({
    enabled: PropTypes.bool,
    maxCharacters: PropTypes.number,
  }),
  multiline: PropTypes.bool,
  rows: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  minRows: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  maxRows: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
};

TextField.defaultProps = {
  ariaDescribedBy: '',
  ariaLabelledBy: '',
  autoComplete: 'on',
  customError: {},
  dataCy: '',
  defaultValue: '',
  disabled: false,
  disableHelperText: false,
  disabledTooltipText: '',
  tooltipContextId: '',
  fullWidth: true,
  id: '',
  isLoading: false,
  label: '',
  onBlurCallback: () => {},
  onChangeCallback: () => {},
  onPaste: () => {},
  placeholder: '',
  required: false,
  subHelperText: <></>,
  type: 'text',
  variant: 'outlined',
  className: undefined,
  dataStyle: false,
  tooltipSev: 'info',
  tooltipMsg: '',
  alertText: null,
  alertClose: true,
  controlFocus: true,
  setControlFocus: () => {},
  enableShrink: false,
  characterCounter: {
    enabled: false,
    maxCharacters: 0,
  },
  multiline: false,
  rows: undefined,
  minRows: undefined,
  maxRows: undefined,
};

export default TextField;
